import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService } from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching available menu types for public menu viewing
 */
export const useAvailableMenuTypes = (subdomain) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  console.log(`[DEBUG-MenuTypes] useAvailableMenuTypes hook called for subdomain ${subdomain}, language ${language} at ${new Date().toISOString()}`);

  return useQuery({
    queryKey: ['availableMenuTypes', subdomain, language],
    queryFn: async () => {
      // Add a timestamp parameter to bypass any HTTP caching
      const timestamp = new Date().getTime();
      console.log(`[DEBUG-MenuTypes] Fetching available menu types for ${subdomain}, language ${language} at ${new Date().toISOString()}`);
      const data = await menuService.getAvailableMenuTypes(subdomain, language, timestamp);
      console.log(`[DEBUG-MenuTypes] Received available menu types:`, data);
      return data;
    },
    staleTime: 0, // Set to 0 to always refetch on mount
    cacheTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: 'always', // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
};

/**
 * Custom hook for fetching and managing public menu data
 * with React Query caching
 */
export const usePublicMenu = (subdomain, menuTypeId = null, month = null, year = null) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  // If month/year not provided, use current month/year
  const today = new Date();
  const currentMonth = month || today.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = year || today.getFullYear();
  
  console.log(`[DEBUG-MenuHook] usePublicMenu called for subdomain: ${subdomain}, menuTypeId: ${menuTypeId}, month: ${currentMonth}, year: ${currentYear}`);

  return useQuery({
    queryKey: ['publicMenu', subdomain, language, menuTypeId, currentMonth, currentYear],
    queryFn: async () => {
      console.log(`[DEBUG-MenuHook] Fetching menu for ${subdomain}, language: ${language}, menuTypeId: ${menuTypeId}, month: ${currentMonth}, year: ${currentYear}`);
      try {
        const data = await menuService.getPublicMenu(subdomain, language, menuTypeId, currentMonth, currentYear);
        console.log(`[DEBUG-MenuHook] Menu data received:`, data);
        return data;
      } catch (error) {
        console.error(`[DEBUG-MenuHook] Error fetching menu:`, error);
        throw error;
      }
    },
    enabled: !!menuTypeId && !!subdomain, // Only run query when we have both menuTypeId and subdomain
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Custom hook for fetching and managing weekly public menu data
 * with React Query caching
 */
export const usePublicWeekMenu = (subdomain, menuTypeId = null, week = null) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  // If week not provided, calculate current week
  const today = new Date();
  let currentWeek = week;
  
  if (!currentWeek) {
    // ISO week number calculation
    const getWeekNumber = (d) => {
      d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
    };
    
    currentWeek = getWeekNumber(today);
  }
  
  console.log(`[DEBUG-MenuHook] usePublicWeekMenu called for subdomain: ${subdomain}, menuTypeId: ${menuTypeId}, week: ${currentWeek}`);

  return useQuery({
    queryKey: ['publicWeekMenu', subdomain, language, menuTypeId, currentWeek],
    queryFn: async () => {
      console.log(`[DEBUG-MenuHook] Fetching weekly menu for ${subdomain}, language: ${language}, menuTypeId: ${menuTypeId}, week: ${currentWeek}`);
      try {
        const data = await menuService.getPublicWeekMenu(subdomain, language, menuTypeId, currentWeek);
        console.log(`[DEBUG-MenuHook] Weekly menu data received from API:`, data);
        // Add detailed logging of the received data structure
        if (data && data.days) {
          console.log(`[DEBUG-MenuHook] Weekly data has ${data.days.length} days. First day example:`, data.days[0]);
          console.log(`[DEBUG-MenuHook] Weekly data weekStart: ${data.weekStart}, weekEnd: ${data.weekEnd}, weekNumber: ${data.weekNumber}`);
        }
        return data;
      } catch (error) {
        console.error(`[DEBUG-MenuHook] Error fetching weekly menu:`, error);
        throw error;
      }
    },
    enabled: !!menuTypeId && !!subdomain, // Only run query when we have both menuTypeId and subdomain
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Custom hook for fetching a single menu for editing
 */
export const useMenu = (menuId, language = 'en') => {
  return useQuery({
    queryKey: ['menu', menuId, language],
    queryFn: () => menuService.getMenu(menuId, language),
    enabled: !!menuId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Custom hook for fetching all menus for a school
 */
export const useMenus = (schoolId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['menus', schoolId, language],
    queryFn: async () => {
      console.log(`[CacheDebug] useMenus hook fetching data for schoolId=${schoolId}, language=${language}`);
      const result = await menuService.getMenus(schoolId);
      console.log(`[CacheDebug] useMenus hook got ${result?.length || 0} menus:`, result);
      return result;
    },
    enabled: !!schoolId,
    staleTime: 1000 * 10, // 10 seconds - much lower to ensure frequent refetches
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
};

/**
 * Custom hook for creating a new menu with optimistic updates
 */
export const useCreateMenu = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  return useMutation({
    mutationFn: (menuData) => menuService.createMenu(menuData),
    onMutate: async (menuData) => {
      // Extract month and year for optimistic update
      const monthStr = menuData.month;
      let month, year;
      
      if (typeof monthStr === 'string' && monthStr.includes('-')) {
        [year, month] = monthStr.split('-').map(num => parseInt(num, 10));
      }
      
      // Make optimistic update to the menus cache if we can
      if (menuData.schoolId && month && year) {
        // Cancel any outgoing refetches so they don't overwrite our optimistic update
        await queryClient.cancelQueries(['menus', menuData.schoolId, language]);
        
        // Get the current menus from cache
        const previousMenus = queryClient.getQueryData(['menus', menuData.schoolId, language]);
        console.log('Previous cached menus before optimistic update:', previousMenus);
        
        if (previousMenus && Array.isArray(previousMenus)) {
          // Create an optimistic menu entry
          const optimisticMenu = {
            id: `temp-${Date.now()}`,
            month: month,
            year: year,
            menu_type: 'Loading...',
            menu_type_id: menuData.menuTypeId,
          };
          
          // Update the cache with our optimistic entry
          queryClient.setQueryData(
            ['menus', menuData.schoolId, language],
            [...previousMenus, optimisticMenu]
          );
          
          console.log('Added optimistic menu to cache:', optimisticMenu);
        }
      }
    },
    onSuccess: (data, variables) => {
      // Log cache state before invalidation
      console.log('[CacheDebug] Before invalidation, menus cache for query key:', ['menus', variables.schoolId, language]);
      
      // Get existing cache data for debugging
      const existingMenusCache = queryClient.getQueryData(['menus', variables.schoolId, language]);
      console.log('[CacheDebug] Existing menus in cache before invalidation:', existingMenusCache);
      
      // Invalidate specific school's menus list
      queryClient.invalidateQueries(['menus', variables.schoolId, language]);
      
      // Invalidate all menus queries to ensure dashboard recent menus is updated
      queryClient.invalidateQueries(['menus']);
      
      // Parse month information for logging
      const monthStr = variables.month;
      let month, year;
      if (typeof monthStr === 'string' && monthStr.includes('-')) {
        [year, month] = monthStr.split('-').map(num => parseInt(num, 10));
      }
      
      // If we have an ID in the response data, invalidate the specific menu cache
      if (data && data.id) {
        queryClient.invalidateQueries(['menu', data.id, language]);
        
        // Log that we're adding the new menu to the cache
        console.log(`[CacheDebug] Parsed menu date: month=${month}, year=${year} from ${monthStr}`);
        
        // Also explicitly update the menus cache with the correct data
        const menus = queryClient.getQueryData(['menus', variables.schoolId, language]);
        console.log('[CacheDebug] Current menus in cache after invalidation but before update:', menus);
        
        if (menus && Array.isArray(menus)) {
          // Format the new menu in the same structure as expected by the components
          const newMenu = {
            id: data.id,
            month: month,
            year: year,
            menu_type: data.menu_type || '',
            menu_type_id: data.menu_type_id || variables.menuTypeId,
          };
          
          console.log('[CacheDebug] Adding new menu to menus cache:', newMenu);
          
          // Remove any temp version of this menu and add the real one
          const updatedMenus = menus
            .filter(m => typeof m.id === 'number' || !String(m.id).startsWith('temp-'))
            .filter(m => !(
              m.month === month && 
              m.year === year && 
              m.menu_type_id === variables.menuTypeId
            ));
          
          // Set the updated cache data
          const newMenusList = [...updatedMenus, newMenu];
          queryClient.setQueryData(
            ['menus', variables.schoolId, language],
            newMenusList
          );
          
          // For debugging: log what's in the cache now
          console.log('[CacheDebug] Updated menus cache after explicit update:', 
            newMenusList
          );
        } else {
          console.log('[CacheDebug] No existing menus cache to update, forcing refetch');
        }
      }
      
      // Also invalidate all public menu data with all language variations
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types cache
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
      
      console.log('[CacheDebug] Invalidated and updated menu caches after creating menu:', {
        newMenuId: data?.id,
        schoolId: variables.schoolId
      });
      
      // Force a refetch to ensure all components have the latest data
      console.log('[CacheDebug] Starting force refetch of menus for schoolId:', variables.schoolId);
      
      // Use refetchQueries AND getQueryData to verify our cache is updated
      queryClient.refetchQueries({
        queryKey: ['menus', variables.schoolId, language],
        exact: true
      }).then(() => {
        // Check cache after refetch
        const afterRefetchCache = queryClient.getQueryData(['menus', variables.schoolId, language]);
        console.log('[CacheDebug] After forced refetch, menus cache now contains:', afterRefetchCache);
        
        // Check if the new menu is in the cache after refetch
        const newMenuInCache = afterRefetchCache?.find(m => m.id === data.id);
        console.log('[CacheDebug] Is new menu in cache after refetch?', !!newMenuInCache, newMenuInCache);
      });
    },
  });
};

/**
 * Custom hook for updating an existing menu with optimistic updates
 */
export const useUpdateMenu = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  return useMutation({
    mutationFn: ({ menuId, menuData }) => menuService.updateMenu(menuId, menuData),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries(['menu', variables.menuId]);
      queryClient.invalidateQueries(['menus']); // Invalidate all menus queries
      // Also invalidate all public menu data with all language variations
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types cache
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
    },
  });
};

/**
 * Custom hook for deleting a menu with optimistic updates
 */
export const useDeleteMenu = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  
  return useMutation({
    mutationFn: (menuId) => {
      console.log(`[DEBUG-MenuTypes] Deleting menu ID ${menuId} at ${new Date().toISOString()}`);
      return menuService.deleteMenu(menuId);
    },
    onSuccess: (data, variables) => {
      console.log(`[DEBUG-MenuTypes] Successfully deleted menu ID ${variables} at ${new Date().toISOString()}`);
      
      // Invalidate all menus queries to refresh data
      console.log(`[DEBUG-MenuTypes] Invalidating menu caches at ${new Date().toISOString()}`);
      queryClient.invalidateQueries(['menus']);
      
      // Invalidate the specific menu cache for the deleted menu
      queryClient.invalidateQueries(['menu', variables, language]);
      
      // Also invalidate all public menu data with all language variations
      console.log(`[DEBUG-MenuTypes] Invalidating publicMenu cache at ${new Date().toISOString()}`);
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'publicMenu'
      });
      
      // Also invalidate available menu types cache
      console.log(`[DEBUG-MenuTypes] Invalidating availableMenuTypes cache at ${new Date().toISOString()}`);
      const availableMenuTypeQueries = queryClient.getQueryCache().findAll({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
      console.log(`[DEBUG-MenuTypes] Found ${availableMenuTypeQueries.length} availableMenuTypes queries to invalidate:`, 
        availableMenuTypeQueries.map(q => q.queryKey));
      
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'availableMenuTypes'
      });
      
      // Force immediate refetch of availableMenuTypes
      console.log(`[DEBUG-MenuTypes] Forcing refetch of availableMenuTypes at ${new Date().toISOString()}`);
      availableMenuTypeQueries.forEach(query => {
        console.log(`[DEBUG-MenuTypes] Refetching query: ${JSON.stringify(query.queryKey)}`);
        queryClient.refetchQueries(query.queryKey, { exact: true });
      });
      
      console.log('Invalidated menu caches after deletion:', {
        deletedMenuId: variables
      });
    },
  });
}; 

/**
 * Hook to fetch recent menus for admin dashboard (limit 5)
 * @param {number} schoolId - The school ID
 * @param {Object} options - React Query options
 * @returns {Object} React Query result object
 */
export const useRecentMenus = (schoolId, options = {}) => {
  const { i18n } = useTranslation();
  
  return useQuery({
    queryKey: ['recent-menus', schoolId, i18n.language],
    queryFn: () => menuService.getRecentMenus(schoolId),
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}; 